import React, { Fragment, useEffect, useState } from 'react';
import FieldControl from 'components/Form/FieldControl';
import cn from 'classnames';
import { uuid } from 'utils/dom';
import { i18n } from 'utils/i18n';
import { safeExecute } from 'utils/validation';
import { types } from '@ehi/global-marketing-interface/lib/utils/js/types';
import { THEMES } from 'constants';

import { propTypes, defaultProps } from './CheckboxPropTypes';

const Checkbox = (props) => {
  const {
    id = null,
    checked = false,
    input,
    value,
    derivedState,
    initialValue,
    onChange,
    vehicleIndex,
    categoryIndex,
    label,
    isRadio,
    dataDtmAnalytics,
    name,
    required,
    disabled,
    theme = THEMES.DARK_BLUE,
    fill,
    meta,
  } = props;

  const generatedId = id || uuid();

  const [checkedState, setCheckedState] = useState(checked || input.value === value);
  const [prevPropValue, setPrevPropValue] = useState(checked ? false : checked);

  // this behaves similar to getDerivedState from props to update the  checkedState value
  useEffect(() => {
    const val = derivedState ? checked || input.value === value : false;
    setCheckedState(val);
  }, [derivedState]);

  // set checked state only if we have initial value , only on first render
  useEffect(() => {
    if (initialValue) {
      input.onChange(initialValue);
      setCheckedState(true);
    }
  }, []);

  // set orupdate checked state value comparing previous and current checked state
  useEffect(() => {
    if (!input) return;
    if (prevPropValue !== checked) {
      setPrevPropValue(checked);
      setCheckedState(checked);
      const val = checked ? input.value : checked;
      input.onChange(val);
    }
  }, [input, checked]);

  const handleToggle = (e) => {
    const { checked: targetChecked, value: targetValue } = e.currentTarget;
    setCheckedState(targetChecked);
    let val;
    if (isRadio) {
      val = targetChecked ? targetValue || targetChecked : targetChecked;
    } else {
      val = targetChecked ? input.value || targetValue || targetChecked : targetChecked;
    }
    input.onChange(val, categoryIndex, vehicleIndex);
    safeExecute(onChange, e, input, val, label);
  };

  const renderLabel = (labelContent) => {
    if (Array.isArray(labelContent)) {
      return labelContent.map((labelText, key) => <Fragment key={key}>{labelText}</Fragment>);
    }
    return labelContent;
  };

  const { invalid, submitFailed } = meta;

  return (
    <div
      className={cn(`checkbox-field ${theme}`, {
        fill,
        required,
        checked: checkedState,
        error: submitFailed && (invalid || (required && !value)),
        disabled,
      })}
    >
      <input
        {...input}
        id={generatedId}
        aria-labelledby={`label-${generatedId}`}
        data-dtm-analytics={dataDtmAnalytics}
        type='checkbox'
        name={name}
        value={value}
        checked={checkedState}
        disabled={disabled}
        onChange={handleToggle}
      />
      <label id={`label-${generatedId}`} htmlFor={generatedId} className='checkbox-field__label'>
        {label && renderLabel(label)}
        {required && <sup>*</sup>}
      </label>
      {required && !checkedState && submitFailed && (
        <span className='checkbox-field__error-message'>
          {types(required).isString ? required : i18n('field_required_dont_forget_to', [label?.toLowerCase()])}
        </span>
      )}
    </div>
  );
};

const CheckboxField = (props) => (
  <FieldControl {...props}>{({ input, meta }) => <Checkbox {...props} input={input} meta={meta} />}</FieldControl>
);

Checkbox.propTypes = propTypes;
Checkbox.defaultProps = defaultProps;

export default CheckboxField;
